import { useMutation } from '@tanstack/react-query'
import { MailPatient } from '../../../model/Mail'
import { TaskCategory } from '../../../model/Task'
import { useCreateTask } from '../task'
import { InsiIdentitySuccessResponse, useInsiIdentityCheck } from './useInsiIdentityCheck.query'
import InsUtilities from '../../../features/ins/utilities/Ins.utilities'
import { InsEventTypeEnums } from '../../../features/ins/enums/insEventType.enums'
import { InsStatusCheckTypeValue } from '../../../features/ins/types/ins.types'
import { ResourceHistoryMutationPayload } from '../../../data/resourceHistory/mapper'
import { createResourceHistory } from '../../../data/resourceHistory/createResourceHistory'
import { InsCheckTypeEnums } from '../../../features/ins/enums/insCheckType.enums'
import { InsStatusCheckTypeEnums } from '../../../features/ins/enums/insStatusCheckType.enums'

export const useIdentityCheckWithTaskCreation = () => {
  const { mutateAsync: createTask } = useCreateTask()
  const { mutateAsync: identityCheck } = useInsiIdentityCheck()

  const mutation = useMutation<InsiIdentitySuccessResponse | null, Error, { patient: MailPatient }>(
    {
      mutationFn: async ({ patient }): Promise<InsiIdentitySuccessResponse | null> => {
        const response = await identityCheck({ patient })
        return response || null
      },
      onSuccess: (
        data: InsiIdentitySuccessResponse | null,
        variables,
      ): InsiIdentitySuccessResponse | null => {
        const { patient } = variables
        const insiPatient = data?.patient || null

        if (insiPatient === null && data?.codeCR) {
          const insLog = InsUtilities.generateNewInsPatientResourceHistory({
            eventType: InsEventTypeEnums.INS_CHECK,
            checkType: InsCheckTypeEnums.PATIENT_IDENTITY,
            patientId: `${patient?.id}`,
            status: data.codeCR as InsStatusCheckTypeValue,
            extraData: {},
          })

          createResourceHistory(insLog as unknown as ResourceHistoryMutationPayload)
          createTask({
            title: patient.id
              ? "Échec de la validation de l'identité via l'INSi"
              : `Échec de la validation de l'identité via l'INSi pour le patient ${patient.birthFirstName} ${patient.birthLastName}`,
            autoCreated: true,
            category: TaskCategory.INFORMATION,
            patientId: patient?.id ?? undefined,
            isNotify: true,
          })
        } else {
          const insLog = InsUtilities.generateNewInsPatientResourceHistory({
            eventType: InsEventTypeEnums.INS_CHECK,
            checkType: InsCheckTypeEnums.PATIENT_IDENTITY,
            status: data?.patient?.codeCR as InsStatusCheckTypeValue,
            patientId: `${patient?.id}`,
            extraData: {},
          })

          createResourceHistory(insLog as unknown as ResourceHistoryMutationPayload)
        }
        return data
      },
      onError: async (error: Error, variables: { patient: MailPatient }) => {
        const { patient } = variables

        await createTask({
          title: patient.id
            ? "Échec de la validation de l'identité via l'INSi"
            : `Échec de la validation de l'identité via l'INSi pour le patient ${patient.birthFirstName} ${patient.birthLastName}`,
          category: TaskCategory.INFORMATION,
          patientId: patient.id ?? undefined,
          autoCreated: true,
          isNotify: true,
        })
        const resourceHistory = InsUtilities.generateNewInsPatientResourceHistory({
          eventType: InsEventTypeEnums.INS_CHECK,
          checkType: InsCheckTypeEnums.PATIENT_IDENTITY,
          status: InsStatusCheckTypeEnums.KO,
          patientId: `${patient?.id}`,
          extraData: {
            errorMessage: (error as unknown as { error: string })?.error,
          },
        })
        await createResourceHistory(resourceHistory)
        return error
      },
    },
  )

  return mutation
}
